/**
 * Eagerly writes the current preorder_text metafield value as a
 * "PRE ORDER Ships by" line item property whenever the cart renders.
 *
 * Triggered on page load and on every cart re-render (cart drawer open,
 * quantity change, etc.) via MutationObserver. Only calls /cart/change.js
 * for line items where the property is missing or stale. Fails silently —
 * a network error never affects the customer experience.
 *
 * Covers all checkout paths including Shop Pay and accelerated checkout.
 */

let isUpdating = false;

async function syncLineItemProperties() {
  if (isUpdating) return;

  const allRows = [...document.querySelectorAll('[data-key]')];
  if (allRows.length === 0) return;

  isUpdating = true;
  try {
    const cartResp = await fetch('/cart.js');
    const cart = await cartResp.json();

    const updates = [];

    for (const row of allRows) {
      const lineItem = cart.items.find((i) => i.key === row.dataset.key);
      if (!lineItem) continue;

      const existingProps = lineItem.properties ?? {};
      const hasProperty = 'PRE ORDER Ships by' in existingProps && existingProps['PRE ORDER Ships by'] !== '';
      const preorderText = row.dataset.preorderText ?? '';

      if (preorderText) {
        // Add or update the property if it's missing or stale.
        if (existingProps['PRE ORDER Ships by'] !== preorderText) {
          updates.push({ key: row.dataset.key, props: { ...existingProps, 'PRE ORDER Ships by': preorderText } });
        }
      } else if (hasProperty) {
        // Remove the property — product is no longer a preorder.
        const cleaned = { ...existingProps };
        delete cleaned['PRE ORDER Ships by'];
        updates.push({ key: row.dataset.key, props: cleaned });
      }
    }

    if (updates.length === 0) return;

    await Promise.all(
      updates.map(({ key, props }) =>
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, properties: props }),
        })
      )
    );
  } catch {
    // Fail silently — property sync is best-effort.
  } finally {
    isUpdating = false;
  }
}

// Run on initial page load (covers cart page).
document.addEventListener('DOMContentLoaded', syncLineItemProperties);

// Re-run whenever the cart re-renders (covers cart drawer and all updates).
// Debounced so rapid DOM mutations collapse into a single run.
let debounceTimer;
new MutationObserver(() => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(syncLineItemProperties, 300);
}).observe(document.body, { childList: true, subtree: true });
